import { cx } from "@suse-o11y/class-variance";
import { IconPin, IconPinCrossed } from "@suse-o11y/icons";
import { useMonitor } from "@suse-o11y/signal";
import React from "react";

import { SkeletonBox } from "~/components/skeleton/box";
import { Tooltip } from "~/components/tooltip";
import { TooltipContent } from "~/components/tooltip/content";
import { TooltipTrigger } from "~/components/tooltip/trigger";

import { useOpenRightPane } from "../../../../../app/state/right-pane/hooks";
import { useRightPaneBoundary } from "../../../../../components/_right-pane-boundary";

import { usePinnedItemsState } from "./_pinned-items-context";
import type { PinnedItemData } from "./PinnedItem";

export interface PinningButtonProps {
	item: undefined | PinnedItemData;
	/**
	 * @default false
	 */
	onlyIcon?: boolean;
	/**
	 * @default false
	 */
	disabled?: boolean;
	className?: string;
}

export const PinningButton: React.FC<PinningButtonProps> = React.memo(
	({ item: itemProp, onlyIcon = false, disabled = false, className }) => {
		const monitor = useMonitor();
		const rightPaneBoundary = useRightPaneBoundary();
		const pinnedItemsState = usePinnedItemsState();
		const openRightPane = useOpenRightPane();

		if (disabled) {
			return <PinningButtonOrIcon className={className} disabled onlyIcon={onlyIcon} />;
		}

		return (
			<SkeletonBox
				className={cx(onlyIcon ? "skeleton:size-3.75" : "skeleton:h-8 skeleton:w-44", className)}
				when={itemProp}
			>
				{(item) => {
					const isPinned = pinnedItemsState.isPinned(monitor, item);

					return (
						<PinningButtonOrIcon
							isPinned={isPinned}
							onClick={(event) => {
								if (isPinned) {
									pinnedItemsState.unpin(item);
								} else {
									pinnedItemsState.pin(item);

									// do not open the tab if the click goes from the right pane
									if (!rightPaneBoundary) {
										openRightPane("pinned-items");
									}
								}

								// the (un)pin click should never propagate to the parent
								event.stopPropagation();
							}}
							onlyIcon={onlyIcon}
						/>
					);
				}}
			</SkeletonBox>
		);
	},
);

const PinningButtonOrIcon: React.FC<{
	isPinned?: boolean;
	onlyIcon: boolean;
	onClick?: React.MouseEventHandler<HTMLButtonElement>;
	disabled?: boolean;
	className?: string;
}> = ({ isPinned, onlyIcon, className, ...props }) => {
	const icon = isPinned ? <IconPinCrossed className="text-15" /> : <IconPin className="text-15" />;
	const text = isPinned ? "Remove from pinned items" : "Add to pinned items";

	if (onlyIcon) {
		return (
			<Tooltip>
				<TooltipTrigger>
					<button type="button" {...props} className={cx("button button-square button-xs", className)}>
						{icon}
					</button>
				</TooltipTrigger>

				<TooltipContent>{text}</TooltipContent>
			</Tooltip>
		);
	}

	return (
		<button type="button" {...props} className={cx("button button-tertiary gap-2", className)}>
			{icon}
			{text}
		</button>
	);
};
